import type { ReactNode } from "react";

type SectionHeaderProps = {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  align?: "center" | "left";
  className?: string;
};

/**
 * Shared marketing section header: eyebrow pill with pulsing brand dot,
 * gradient headline, and muted description (schedule-template style).
 */
export function SectionHeader({
  eyebrow,
  title,
  description,
  align = "center",
  className = "",
}: SectionHeaderProps) {
  const centered = align === "center";

  return (
    <div className={`${centered ? "text-center" : "text-left"} ${className}`}>
      <span className="eyebrow-pill">{eyebrow}</span>
      <h2
        className={`heading-gradient mt-4 max-w-3xl text-balance text-3xl font-semibold tracking-tight sm:text-4xl ${
          centered ? "mx-auto" : ""
        }`}
      >
        {title}
      </h2>
      {description ? (
        <p
          className={`mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg ${
            centered ? "mx-auto" : ""
          }`}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Small "+" intersection marker used at section container corners
 * (signature detail of the schedule marketing template).
 */
export function CornerCross({ className = "" }: { className?: string }) {
  return (
    <span
      className={`pointer-events-none absolute z-10 h-4 w-4 ${className}`}
      aria-hidden
    >
      <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[var(--cross-color)]" />
      <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-[var(--cross-color)]" />
    </span>
  );
}
